import { useState, useEffect, useCallback } from 'react';
import { Loader2, Banknote, TrendingUp, Megaphone, Percent, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fetchCostStats, type CostStatsResponse } from '@/services/adminCostService';

type Range = 7 | 30 | 90;

// ============================================================================
// OpenAI GPT Image 2 pricing per 1M tokens (2026-04, source: developers.openai.com/api/docs/pricing)
// Reference rates — actual per-call cost is computed server-side in usage-tracker
// from api_pricing snapshots, so these values are informational only.
// ============================================================================
const OPENAI_PRICING = {
  imageInputPer1M:       8.00,
  imageInputCachedPer1M: 2.00,
  imageOutputPer1M:     30.00,
  textInputPer1M:        5.00,
  textInputCachedPer1M:  1.25,
};

/**
 * Estimate OpenAI GPT Image 2 cost from token usage.
 * Assumes all input is image tokens (no text/image split in api_usage);
 * this slightly over-estimates when text is used but remains safe and
 * matches the assumption documented alongside OPENAI_PRICING.
 */
export function calculateOpenAICostUSD(usage: {
  input_tokens: number | null;
  output_tokens: number | null;
  cached_tokens: number | null;
}): number {
  const input = (usage.input_tokens ?? 0) / 1_000_000;
  const output = (usage.output_tokens ?? 0) / 1_000_000;
  const cached = (usage.cached_tokens ?? 0) / 1_000_000;
  return (input - cached) * OPENAI_PRICING.imageInputPer1M
       + cached * OPENAI_PRICING.imageInputCachedPer1M
       + output * OPENAI_PRICING.imageOutputPer1M;
}

function rangeDates(days: Range): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - days);
  return { from, to };
}

function fmtEur(n: number): string {
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
}

function fmtPct(n: number): string {
  return `${n.toFixed(1)}%`;
}

interface KPICardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: 'indigo' | 'amber' | 'rose' | 'green';
  isLoading: boolean;
}

const colorClasses = {
  indigo: 'bg-primary/10 text-primary',
  amber: 'bg-amber-500/10 text-amber-500',
  rose: 'bg-rose-500/10 text-rose-500',
  green: 'bg-emerald-500/10 text-emerald-500',
};

const KPICard: React.FC<KPICardProps> = ({ title, value, icon, color, isLoading }) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mt-1" /> : <p className="text-2xl font-bold text-foreground">{value}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Minimal SVG line chart: three series (cogs, marketing, revenue) over daily points.
interface MiniChartProps {
  data: CostStatsResponse['daily_series'];
}
const MiniChart: React.FC<MiniChartProps> = ({ data }) => {
  if (data.length === 0) return <p className="text-sm text-muted-foreground">Keine Daten im Zeitraum.</p>;
  const W = 800, H = 200, PAD = 24;
  const maxY = Math.max(1, ...data.flatMap(d => [d.cogs_eur, d.marketing_eur, d.revenue_eur]));
  const x = (i: number) => PAD + (i / Math.max(1, data.length - 1)) * (W - 2 * PAD);
  const y = (v: number) => H - PAD - (v / maxY) * (H - 2 * PAD);
  const toPath = (key: 'cogs_eur' | 'marketing_eur' | 'revenue_eur') =>
    data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-48">
      <rect x={PAD} y={PAD} width={W - 2 * PAD} height={H - 2 * PAD} fill="none" stroke="currentColor" className="text-muted-foreground/20" />
      <path d={toPath('revenue_eur')}    fill="none" stroke="currentColor" className="text-emerald-500" strokeWidth="2" />
      <path d={toPath('cogs_eur')}       fill="none" stroke="currentColor" className="text-amber-500"   strokeWidth="2" />
      <path d={toPath('marketing_eur')}  fill="none" stroke="currentColor" className="text-rose-500"    strokeWidth="2" />
      <text x={PAD} y={PAD - 6} className="text-xs fill-current text-muted-foreground">max: {fmtEur(maxY)}</text>
    </svg>
  );
};

export const AdminCostView: React.FC = () => {
  const [range, setRange] = useState<Range>(30);
  const [data, setData] = useState<CostStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = rangeDates(range);
      const result = await fetchCostStats(from, to);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2"><Banknote className="w-6 h-6" /> Kosten & Marge</h2>
        <div className="flex gap-2">
          {[7, 30, 90].map(n => (
            <Button key={n} size="sm" variant={range === n ? 'default' : 'outline'} onClick={() => setRange(n as Range)}>
              {n}T
            </Button>
          ))}
        </div>
      </div>

      {error && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard title="Umsatz"    value={data ? fmtEur(data.kpis.revenue_eur) : '—'}           icon={<TrendingUp />} color="indigo" isLoading={loading} />
        <KPICard title="COGS"      value={data ? fmtEur(data.kpis.cogs_cost_eur) : '—'}         icon={<Banknote />}   color="amber"  isLoading={loading} />
        <KPICard title="Marketing" value={data ? fmtEur(data.kpis.marketing_cost_eur) : '—'}    icon={<Megaphone />}  color="rose"   isLoading={loading} />
        <KPICard title="Marge"     value={data ? fmtPct(data.kpis.margin_pct) : '—'}            icon={<Percent />}    color="green"  isLoading={loading} />
      </div>

      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Tagesverlauf</h3>
          {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : data ? <MiniChart data={data.daily_series} /> : null}
          <div className="flex gap-4 text-xs mt-3 text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-500 inline-block" /> Umsatz</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-500 inline-block" /> COGS</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-rose-500 inline-block" /> Marketing</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Info className="w-4 h-4 text-muted-foreground" />
            OpenAI GPT Image 2 – Referenzpreise
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            Pro 1M Tokens (Stand 2026-04). Tatsächliche Kosten werden serverseitig aus <code className="text-[11px]">api_pricing</code> berechnet.
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
            <li className="flex justify-between"><span className="text-muted-foreground">Image Input</span><span className="font-mono">${OPENAI_PRICING.imageInputPer1M.toFixed(2)}</span></li>
            <li className="flex justify-between"><span className="text-muted-foreground">Image Input (cached)</span><span className="font-mono">${OPENAI_PRICING.imageInputCachedPer1M.toFixed(2)}</span></li>
            <li className="flex justify-between"><span className="text-muted-foreground">Image Output</span><span className="font-mono">${OPENAI_PRICING.imageOutputPer1M.toFixed(2)}</span></li>
            <li className="flex justify-between"><span className="text-muted-foreground">Text Input</span><span className="font-mono">${OPENAI_PRICING.textInputPer1M.toFixed(2)}</span></li>
            <li className="flex justify-between"><span className="text-muted-foreground">Text Input (cached)</span><span className="font-mono">${OPENAI_PRICING.textInputCachedPer1M.toFixed(2)}</span></li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Nach Function</h3>
          {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : (
            <ul className="space-y-2">
              {(data?.breakdown_by_function ?? []).map(b => (
                <li key={b.function_name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${b.category === 'cogs' ? 'bg-amber-500/10 text-amber-600' : 'bg-rose-500/10 text-rose-600'}`}>{b.category}</span>
                    <span className="font-mono">{b.function_name}</span>
                    <span className="text-muted-foreground">· {b.call_count} Calls</span>
                  </div>
                  <span className="font-semibold">{fmtEur(b.cost_eur)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Top 10 User nach Kosten</h3>
          {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Email</th>
                  <th className="py-1 text-right">Calls</th>
                  <th className="py-1 text-right">Kosten</th>
                  <th className="py-1 text-right">Gekauft</th>
                  <th className="py-1 text-right">Netto-Marge</th>
                </tr>
              </thead>
              <tbody>
                {(data?.top_users ?? []).map(u => (
                  <tr key={u.user_id} className="border-t border-border">
                    <td className="py-2">{u.email || <span className="text-muted-foreground">—</span>}</td>
                    <td className="py-2 text-right">{u.call_count}</td>
                    <td className="py-2 text-right">{fmtEur(u.total_cost_eur)}</td>
                    <td className="py-2 text-right">{fmtEur(u.credits_purchased_eur)}</td>
                    <td className={`py-2 text-right font-semibold ${u.net_margin_eur < 0 ? 'text-rose-500' : 'text-emerald-500'}`}>{fmtEur(u.net_margin_eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
